/* 
 * API service for email classification
 * This file contains simulated API calls that will be replaced with real API calls in the future
 */

// Interface for the analysis response
export interface AnalysisResponse {
  referenceFound: boolean;
  detectedReference?: string;
  suggestedFolders: {
    name: string;
    confidence: number;
    reference: string;
  }[];
}

// Interface for the save reference response
export interface SaveReferenceResponse {
  success: boolean;
  message: string;
}

/**
 * Makes an API call to analyze an email
 * @param emailId The ID of the email to analyze
 * @returns Promise with analysis results
 */
export const analyzeEmail = async (emailId: string): Promise<AnalysisResponse> => {
  console.log(`Analyzing email with ID: ${emailId}`);
  
  // Try to make the actual API call to the endpoint
  try {
    const response = await fetch('https://classification-trib-api.vercel.app/analyze', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin':"*"
        
      },
      body: JSON.stringify({ "internet_message_id": emailId })
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // Check if the response contains an error message
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Handle the two different response formats
    if (data.file_DN_refrence) {
      // Direct reference found
      return {
        referenceFound: true,
        detectedReference: data.file_DN_refrence,
        suggestedFolders: []
      };
    } else if (data.top_folders) {
      // Suggested folders with references
      return {
        referenceFound: false,
        suggestedFolders: data.top_folders.map(folder => ({
          name: `Folder ${folder.reference}`, // Using reference as folder name
          confidence: folder.probability , // Convert percentage to decimal
          reference: folder.reference
        }))
      };
    } else {
      throw new Error('Unexpected API response format');
    }
  } 
  catch (error) {
    console.error('Error in analyzeEmail function:', error);
    throw new Error();
    
  }
};

/**
 * Validates if a reference follows the required format: DT-XXXX-XX
 * @param reference The reference to validate
 * @returns boolean indicating if the reference is valid
 */
export const validateReference = (reference: string): boolean => {
  // Format: DT-XXXX-XX where X is any alphanumeric character
  const referenceRegex = /^DT-[A-Za-z0-9]{4}-[A-Za-z0-9]{2}$/;
  return referenceRegex.test(reference);
};

/**
 * Interface for the move email request
 */
export interface MoveEmailRequest {
  internet_message_id: string;
  folder_reference: string;
  user_password: string;
}

/**
 * Makes an API call to move an email to a folder
 * @param emailId The ID of the email
 * @param reference The folder reference
 * @param password The user password (security code)
 * @returns Promise with move result
 */
export const moveEmail = async (emailId: string, reference: string, password: string): Promise<SaveReferenceResponse> => {
  console.log(`Moving email with ID: ${emailId} to folder with reference: ${reference}`);
  
  try {
    const response = await fetch('https://classification-trib-api.vercel.app/move-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*'
      },
      body: JSON.stringify({
        internet_message_id: emailId,
        folder_reference: reference,
        user_password: password
      })
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // Check if the response contains an error message
    if (data.error) {
      return {
        success: false,
        message: data.error
      };
    }
    if (data.status=="success") {
      return {
        success: true,
        message: data.message || "Email moved successfully"
      };
    }
    else {
      return {
        success: false,
        message: data.message || "Email moved failed"
      };
    } 
    
  } catch (error) {
    console.error('Error in moveEmail function:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to move email"
    };
  }
};

/**
 * Makes an API call to save a reference for an email
 * @param emailId The ID of the email
 * @param reference The reference to save
 * @param password The user password (security code)
 * @returns Promise with save result
 */
export const saveReference = async (emailId: string, reference: string, password: string ): Promise<SaveReferenceResponse> => {
  console.log(`Saving reference ${reference} for email ID: ${emailId}`);
  
  // Validate the reference format
  if (!validateReference(reference)) {
    return {
      success: false,
      message: "Invalid reference format. Please use format DT-XXXX-XX"
    };
  }
  
  // If password is provided, use the moveEmail API
    return moveEmail(emailId, reference, password);
  
  
 
};